import { useCallback, useEffect, useRef, useState } from "react";

/**
 * One instance per copyable control: copies a value to the clipboard and
 * exposes a short-lived `copied` flag for a temporary success indicator.
 * Clipboard failures (permission denied, unsupported environment, etc.)
 * are caught and reported via the returned boolean rather than thrown —
 * callers never need a try/catch of their own.
 */
export function useCopyToClipboard(resetDelayMs = 1500) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const copy = useCallback(
    async (value) => {
      try {
        if (!navigator?.clipboard?.writeText) {
          throw new Error("Clipboard API unavailable");
        }
        await navigator.clipboard.writeText(value);
        setCopied(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), resetDelayMs);
        return true;
      } catch {
        return false;
      }
    },
    [resetDelayMs]
  );

  return { copied, copy };
}

export default useCopyToClipboard;
